import { useSearchParams } from "react-router";
import {
  archiveTemplateAction,
  discardTemplateAction,
  restoreTemplateAction,
} from "@/actions/templates";
import { useApiQuery } from "@/api/useApiQuery";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { PlusIcon } from "@/components/Icon";
import { LinkButton } from "@/components/LinkButton";
import { ListingSummary } from "@/components/ListingSummary";
import { PageHeader } from "@/components/PageHeader";
import { RowActions } from "@/components/RowActions";
import { StatusBadge } from "@/components/StatusBadge";
import { sortableKeys, sortRows } from "@/lib/sortRows";
import type { QuestListRow, QuestTemplateListRow } from "@shared/dto";
import { EFFORTS } from "@shared/validation";
import { QuestName } from "./quests/QuestName";
import { QuestTabs } from "./quests/QuestTabs";
import {
  buildSortHref,
  isTemplateInView,
  parseOpenKeys,
  parseSort,
  parseView,
  QUEST_SORT_PARAM,
  type QuestsSearchParams,
  type QuestView,
  TEMPLATE_SORT_PARAM,
} from "./quests/questsUrl";
import { RetiredMark } from "./quests/RetiredMark";
import { RungPanel } from "./quests/RungPanel";
import { StatusFilter } from "./quests/StatusFilter";
import { TemplateToggle } from "./quests/TemplateToggle";

const TEMPLATE_STATUS_DRAFT = "DRAFT";
const TEMPLATE_STATUS_PUBLISHED = "PUBLISHED";
const TEMPLATE_STATUS_ARCHIVED = "ARCHIVED";
const RUNG_STATUS_ACTIVE = "ACTIVE";
const RUNG_STATUS_ARCHIVED = "ARCHIVED";

const dateFormatter = new Intl.DateTimeFormat("en-GB", { dateStyle: "medium" });

const numberFormatter = new Intl.NumberFormat("en-GB", {
  maximumFractionDigits: 3,
});

// target_value comes back as numeric(12,3), so "10000.000" without formatting.
const formatTarget = (value: string | null): string =>
  value === null ? "—" : numberFormatter.format(Number(value));

// Statuses and efforts are lifecycles and sizes, not word lists: sorted as
// text, ARCHIVED would lead the templates and QUICK would sit between MEDIUM
// and SHORT. Sorting by position in the domain's own order says what the
// column means.
const TEMPLATE_STATUS_ORDER = [
  TEMPLATE_STATUS_DRAFT,
  TEMPLATE_STATUS_PUBLISHED,
  TEMPLATE_STATUS_ARCHIVED,
];

const QUEST_STATUS_ORDER = [RUNG_STATUS_ACTIVE, RUNG_STATUS_ARCHIVED];

// An unknown value sorts last rather than first, where -1 would put it.
const rankOf = (order: readonly string[], value: string): number => {
  const index = order.indexOf(value);

  return index === -1 ? order.length : index;
};

const buildTemplateColumns = (
  searchParams: QuestsSearchParams,
  openKeys: string[],
  rungsByTemplate: Map<number, QuestListRow[]>,
): DataTableColumn<QuestTemplateListRow>[] => [
  {
    key: "title",
    label: "Title",
    info: "The plain default-locale label, capped at 40 characters and never formatted. The task itself is the assignment — open the row to see it rendered at each rung.",
    isProse: true,
    sortValue: (row) => row.title,
    render: (row) => (
      <TemplateToggle
        id={row.id}
        title={row.title ?? "—"}
        searchParams={searchParams}
        openKeys={openKeys}
        hasRungs={(rungsByTemplate.get(row.id) ?? []).length > 0}
      />
    ),
  },
  {
    key: "topicCode",
    label: "Topic",
    sortValue: (row) => row.topicCode,
    render: (row) =>
      row.topicCode === null ? (
        "—"
      ) : (
        <RetiredMark
          value={row.topicCode}
          isRetired={row.isTopicInactive}
          note="inactive"
        />
      ),
  },
  {
    key: "goalType",
    label: "Goal",
    info: "THRESHOLD counts up to a target, MANUAL is self-reported with no number. Repetition is a rung’s own axis, not a goal type.",
    sortValue: (row) => row.goalType,
    render: (row) => row.goalType,
  },
  {
    key: "status",
    label: "Status",
    sortValue: (row) => rankOf(TEMPLATE_STATUS_ORDER, row.status),
    render: (row) => (
      <StatusBadge
        label={row.status}
        tone={row.status === "PUBLISHED" ? "accent" : "outline"}
      />
    ),
  },
  {
    key: "updatedAt",
    label: "Updated",
    // A string over the wire, so it is parsed here rather than sorted as text:
    // ISO 8601 happens to sort correctly, but the column means a moment.
    sortValue: (row) => new Date(row.updatedAt).getTime(),
    render: (row) => dateFormatter.format(new Date(row.updatedAt)),
  },
];

const questColumns: DataTableColumn<QuestListRow>[] = [
  {
    key: "title",
    label: "Quest",
    info: "The template's title, then its assignment with this rung's numbers substituted — formatted through ICU so the plural is right at every rung.",
    isProse: true,
    sortValue: (row) => row.title,
    render: (row) => (
      <QuestName title={row.title} assignment={row.assignment} />
    ),
  },
  {
    key: "topicCode",
    label: "Topic",
    sortValue: (row) => row.topicCode,
    render: (row) =>
      row.topicCode === null ? (
        "—"
      ) : (
        <RetiredMark
          value={row.topicCode}
          isRetired={row.isTopicInactive}
          note="inactive"
        />
      ),
  },
  {
    key: "skills",
    label: "Skills",
    info: "Skills this quest's template grants. Progression counts distinct completed templates per skill.",
    // The first skill, because that is the one the cell leads with and the
    // list is already in code order.
    sortValue: (row) => row.skills[0]?.name ?? null,
    render: (row) =>
      row.skills.length === 0
        ? "—"
        : row.skills.map((skill, index) => (
            <span key={skill.name}>
              {index === 0 ? null : ", "}
              <RetiredMark
                value={skill.name}
                isRetired={skill.isArchived}
                note="archived"
              />
            </span>
          )),
  },
  {
    key: "targetValue",
    label: "Target",
    // Numeric, not the formatted string: "1,000" would sort under "999".
    sortValue: (row) =>
      row.targetValue === null ? null : Number(row.targetValue),
    render: (row) => formatTarget(row.targetValue),
  },
  {
    key: "requiredDays",
    label: "Required days",
    sortValue: (row) => row.requiredDays,
    render: (row) => row.requiredDays ?? "—",
  },
  {
    key: "difficultyLevel",
    label: "Difficulty",
    sortValue: (row) => row.difficultyLevel,
    render: (row) => row.difficultyLevel,
  },
  {
    key: "effort",
    label: "Effort",
    sortValue: (row) => rankOf(EFFORTS, row.effort),
    render: (row) => row.effort,
  },
  {
    key: "status",
    label: "Status",
    sortValue: (row) => rankOf(QUEST_STATUS_ORDER, row.status),
    render: (row) => (
      <StatusBadge
        label={row.status}
        tone={row.status === "ACTIVE" ? "accent" : "neutral"}
      />
    ),
  },
];

export const QuestsPage = () => {
  const [params] = useSearchParams();
  const templatesQuery = useApiQuery<QuestTemplateListRow[]>("/templates");
  const questsQuery = useApiQuery<QuestListRow[]>("/quests");

  const templates = templatesQuery.data ?? [];
  const quests = questsQuery.data ?? [];
  const isLoading = templatesQuery.isLoading || questsQuery.isLoading;

  // The URL helpers were written against Next's flat search-param object, and
  // they still read that way: one shape in, so the parsing rules stay in one
  // place whatever produced them.
  const searchParams: QuestsSearchParams = Object.fromEntries(params);

  const openKeys = parseOpenKeys(searchParams.open);
  const view = parseView(searchParams.view);
  const isArchivedView = view === "ARCHIVED";

  // Filtered here rather than in the query: both listings already load in
  // full for the counts and for the rung grouping, so a second round trip
  // would buy nothing.
  const shownTemplates = templates.filter((row) =>
    isTemplateInView(row.status, view),
  );

  // Rungs follow their template rather than carrying the view themselves — a
  // rung has only ACTIVE and ARCHIVED, so "show me the drafts" can only mean
  // the rungs of draft templates. The archived view is the exception: it
  // gathers every retired rung, including those under templates that are still
  // live, which would otherwise be visible in no view at all.
  const shownTemplateIds = new Set(shownTemplates.map((row) => row.id));
  const shownQuests = isArchivedView
    ? quests.filter((row) => row.status === RUNG_STATUS_ARCHIVED)
    : quests.filter(
        (row) =>
          shownTemplateIds.has(row.templateId) &&
          row.status !== RUNG_STATUS_ARCHIVED,
      );

  const countByStatus = (status: string): number =>
    templates.filter((row) => row.status === status).length;

  const viewCounts: Record<QuestView, number> = {
    ACTIVE: templates.filter((row) => row.status !== TEMPLATE_STATUS_ARCHIVED)
      .length,
    DRAFT: countByStatus("DRAFT"),
    PUBLISHED: countByStatus("PUBLISHED"),
    ARCHIVED: countByStatus(TEMPLATE_STATUS_ARCHIVED),
  };

  // Grouped rather than queried per template: this page already loads every
  // quest for the Quests tab, so the rungs are in hand. Grouped from the
  // filtered list so an expanded row agrees with the Quests tab, and grouped
  // before the Quests tab is sorted — a ladder only reads as a ladder in
  // difficulty order, whatever column the tab beside it is sorted by.
  const rungsByTemplate = Map.groupBy(shownQuests, (quest) => quest.templateId);

  const templateColumns = buildTemplateColumns(
    searchParams,
    openKeys,
    rungsByTemplate,
  );

  // An explicit sort replaces the default order outright rather than layering
  // on top of it: archived templates stop sinking once you sort by title. The
  // view above the table is the control for hiding them, and a sort that
  // quietly kept a second rule would not be the sort the header names.
  const templateSort = parseSort(
    searchParams[TEMPLATE_SORT_PARAM],
    sortableKeys(templateColumns),
  );
  const questSort = parseSort(
    searchParams[QUEST_SORT_PARAM],
    sortableKeys(questColumns),
  );

  const refresh = () => {
    templatesQuery.refresh();
    questsQuery.refresh();
  };

  return (
    <>
      <PageHeader
        kicker="Catalog"
        title="Quests"
        action={
          <LinkButton href="/quests/templates/new" variant="primary">
            <PlusIcon size={14} />
            New template
          </LinkButton>
        }
      />
      <ListingSummary
        counts={[
          {
            label: "template",
            value: shownTemplates.length,
            total: templates.length,
          },
          { label: "quest", value: shownQuests.length, total: quests.length },
        ]}
        filters={
          <StatusFilter
            searchParams={searchParams}
            selected={view}
            counts={viewCounts}
          />
        }
      />
      <QuestTabs
        templates={
          <DataTable
            label="Quest templates"
            columns={templateColumns}
            rows={sortRows(shownTemplates, templateColumns, templateSort)}
            getRowKey={(row) => String(row.id)}
            sort={templateSort}
            buildSortHref={(key) =>
              buildSortHref(
                searchParams,
                TEMPLATE_SORT_PARAM,
                key,
                templateSort,
              )
            }
            emptyMessage={
              isLoading
                ? "Loading..."
                : view === "ACTIVE"
                  ? "No active quest templates."
                  : `No ${view.toLowerCase()} quest templates.`
            }
            expandedKeys={openKeys}
            renderExpandedRow={(row) => (
              <RungPanel rungs={rungsByTemplate.get(row.id) ?? []} />
            )}
            rowActions={(row) => (
              <RowActions
                editHref={`/quests/templates/${row.id}`}
                isInactive={row.status === TEMPLATE_STATUS_ARCHIVED}
                retireLabel="Archive"
                retirePendingLabel="Archiving..."
                confirmTitle="Archive this template?"
                confirmBody={
                  <>
                    <p>
                      It drops out of the offer immediately. Its rungs and every
                      attempt against them keep resolving, which is why nothing
                      here is deleted.
                    </p>
                    <p>
                      Restoring puts it back as a draft, so publishing it again
                      runs the full gate.
                    </p>
                  </>
                }
                retireAction={archiveTemplateAction.bind(null, row.id)}
                restoreAction={restoreTemplateAction.bind(null, row.id)}
                onCompleted={refresh}
                // No archive step first: a template nobody ever saw has no
                // history to retire, so archiving it would be ceremony
                // carrying no information. An ever-published one gets no
                // button at all — its revision history is append-only, so
                // archive is its terminal state.
                deleteLabel="Discard"
                deletePendingLabel="Discarding..."
                deleteConfirmTitle="Discard this template?"
                deleteConfirmBody={
                  row.publishedAt === null ? (
                    <>
                      <p>
                        The template goes for good, along with its rungs,
                        wording and skill and trait bindings, and its code
                        becomes available again. Possible only because it was
                        never published — no user ever saw it.
                      </p>
                      <p>This cannot be undone.</p>
                    </>
                  ) : undefined
                }
                deleteAction={
                  row.publishedAt === null
                    ? discardTemplateAction.bind(null, row.id)
                    : undefined
                }
              />
            )}
          />
        }
        quests={
          <DataTable
            label="Quests"
            columns={questColumns}
            rows={sortRows(shownQuests, questColumns, questSort)}
            getRowKey={(row) => String(row.id)}
            sort={questSort}
            buildSortHref={(key) =>
              buildSortHref(searchParams, QUEST_SORT_PARAM, key, questSort)
            }
            emptyMessage={
              isLoading
                ? "Loading..."
                : isArchivedView
                  ? "No archived quests."
                  : `No quests under ${view.toLowerCase()} templates.`
            }
          />
        }
      />
    </>
  );
};
